import Vue from 'vue';
import $http from 'middleware/http';
import selfCheckDefaults from './self-check-defaults';
import EventManager from 'dd-event-manager';
import subjectSelectionValidater from './rules/subject-selection-validater';
import parseXml from './parseXml';



const ev = {};
new EventManager(ev);


const GROUPABLE_CATEGORIES = [
  {targets: ['1', '2']},
];

const FIRST_CHOICE_SUBJECT_SELECTABLE_CATEGORIES = {
  '1_2': {},
  '6'  : {},
};

// 総合特定算出時、複数科目で合計点をまとめるグループ定義
// グループ内で全ての科目を選択している時のみ丸める
const SCORE_ROUND_GROUPS = {
  // '301': ['301', '311'], // 英語・筆記、リスニング
};



export default function factory(year) {
  return {
    namespaced: true,

    state: {
      year,
      loadState: 'pending',
      categories: [],
      subjects: [],
      childSubjects: {},
    },

    getters: {

      // **************************************************
      // settings
      // **************************************************
      year: state => state.year,
      lsModuleName: (state, getters, rootState, rootGetters) => (getters['year'] === rootGetters['config/year'] ? 'ls' : 'ls-' + getters['year']),
      segmentSync: (state, getters, rootState, rootGetters) => getters['year'] === rootGetters['config/year'],
      isLoading: state => state.loadState === 'loading',
      isLoaded: state => state.loadState === 'loaded',
      isLoadFailid: state => state.loadState === 'failid',
      dataDirName: (state, getters, rootState, rootGetters) => rootGetters['config/year'] === getters['year'] ? 'center' : `dn${getters['year']}`,
      dataDirPath: (state, getters, rootState, rootGetters) => rootGetters['path/$join']('resources', 'data/selfcheck/' + getters['dataDirName']),
      subjectsFilePath: (state, getters) => `${getters['dataDirPath']}/subjects.xml`,
      selectedSubjects: (state, getters, rootState, rootGetters) => {
        return getters['subjects'].filter(subject => {
          const history = rootGetters[`${getters['lsModuleName']}/selfcheckSubject`](subject.id);
          return !!history && history.selected;
        });
      },



      // **************************************************
      // categories
      // **************************************************
      categories: (state, getters) => {
        return state.categories.map(category => Object.assign({}, category, {
          subjects: getters['subjectsByCategoryId'](category.id),
        }));
      },

      groupedCategoryById: (state, getters) => id => getters['groupedCategories'].find(category => category.id === id),

      groupedCategories: (state, getters) => {
        const categories = getters['categories'].slice();

        GROUPABLE_CATEGORIES.forEach(define => {
          const splicedCategories = [];
          let firstIndex = null;

          define.targets.forEach(targetId => {
            const target = categories.find(category => category.id === targetId),
                  index  = categories.indexOf(target);

            if (!target) return;

            if (firstIndex === null) firstIndex = index;

            categories.splice(index, 1);
            splicedCategories.push(target);
          });

          const mergedCategory = {
            id      : [],
            name    : [],
            subjects: [],
          };

          splicedCategories.forEach(category => {
            mergedCategory.id.push(category.id);
            mergedCategory.name.push(category.name);
            mergedCategory.subjects = mergedCategory.subjects.concat(category.subjects);
          });

          mergedCategory.id = mergedCategory.id.join('_');
          mergedCategory.name = mergedCategory.name.join('・');

          categories.splice(firstIndex, 0, mergedCategory);
        });

        for (let category of categories) {
          category.firstChoiceSelectable = FIRST_CHOICE_SUBJECT_SELECTABLE_CATEGORIES[category.id] !== undefined;
        }

        return categories;
      },

      subjectsByCategoryId: (state, getters) => categoryId => getters['subjects'].filter(subject => subject.categoryId === categoryId),



      // **************************************************
      // subjects
      // **************************************************
      subjects: (state, getters) => state.subjects.map(subject => Object.assign({}, subject, {
        children: getters['childSubjectsBySubjectId'](subject.id) || [],
      })),

      selectedSubjectIds: (state, getters) => getters['selectedSubjects'].map(subject => subject.id),

      childSubjectsBySubjectId: (state, getters) => subjectId => getters['childSubjects'][subjectId],

      childSubjects: (state, getters) => {
        const childSubjects = {};
        for (let subjectId in state.childSubjects) {
          childSubjects[subjectId] = state.childSubjects[subjectId].map(child => {
            return Object.assign({}, child);
          });
        }
        return childSubjects;
      },

      subjectsByState: (state, getters) => state => {
        return getters['subjects'].filter(subject => {
          const history = getters['subjectHistory'](subject.id);
          return history.state === state;
        });
      },

      fixedSubjects: (state, getters) => getters['subjectsByState']('fixed'),
      indeterminateSubjects: (state, getters) => getters['subjectsByState']('indeterminate'),

      subjectOrChildById: (state, getters) => id => {
        const subjects = getters['subjects'];
        let subject = subjects.find(subject => subject.id === id);
        if (subject) return subject;

        for (let s of subjects) {
          for (let c of s.children || []) {
            if (c.id === id) return c;
          }
        }
      },

      // API送信用のパラメータを取得
      // 合計点数は英語リスニングや、サブ科目にかかわらずそのまま送信
      // 入力情報はサブ科目や直接入力の場合含めない
      subjectInputsApiParams: (state, getters) => id => {
        const params = {};
        const subject = getters['subjectOrChildById'](id);
        const isParent = !subject.subjectId;
        const history = getters['subjectHistory'](id);
        params[`sum_${id}`] = history.score;

        if (isParent) {
          if (history.isSelfCheck) {
            const inputs = history.inputs || {};
            for (let key in inputs) {
              let value = inputs[key];
              if (value == 'null') {
                value = '';
              } else if (value == '-') {
                value = 'M';
              } else  if (value == '±') {
                value = 'P';
              }
              params[`${id}_${key}`] = value;
            }
          }
          if (subject.children) {
            for (let child of subject.children) {
              const history = getters['subjectHistory'](child.id);
              params[`sum_${child.id}`] = history.score;
            }
          }
        }
        return params;
      },

      subjectSegmentParams: (state, getters) => id => {
        const history = getters['subjectHistory'](id);
        return {
          id,
          type     : history.type,
          state    : history.fixed ? 'fixed' : (history.selected ? 'selected' : 'pending'),
          direct   : history.directScore,
          selfcheck: history.selfcheckScore,
        }
      },



      // **************************************************
      // histories
      // **************************************************
      subjectHistory: (state, getters, rootState, rootGetters) => (id, opts = {}) => {
        const subject = getters['subjectOrChildById'](id);
        const isChild = !!subject.subjectId;
        const advanceParams = opts.advanceParams === undefined ? true : opts.advanceParams;
        const mergeParent = opts.mergeParent === undefined ? true : opts.mergeParent;
        const needMerge = isChild && mergeParent;

        const parentHistory = needMerge ? rootGetters[`${getters['lsModuleName']}/selfcheckSubject`](subject.subjectId) || selfCheckDefaults() : null;
        let history = rootGetters[`${getters['lsModuleName']}/selfcheckSubject`](id) || selfCheckDefaults();

        if (needMerge) {
          history = Object.assign({}, history, {
            type: parentHistory.type,
            selected: parentHistory.selected,
            selfCheckIsFinished: parentHistory.selfCheckIsFinished,
          });
        }

        if (advanceParams) {
          const isSelfCheck = history.type === 'selfcheck';
          const isDirect = history.type === 'direct';
          const score = history[`${history.type}Score`];
          const hasScore = typeof score === 'number';
          const fixed = isSelfCheck ? history.selfCheckIsFinished : hasScore;
          const state = history.selected ? fixed ? 'fixed' : 'indeterminate' : 'pending';

          history = Object.assign({}, history, {
            isSelfCheck,
            isDirect,
            score,
            hasScore,
            fixed,
            state,
          });
        }
        return history;
      },

      groupedCategoryFirstChoice: (state, getters, rootState, rootGetters) => categoryId => {
        return rootGetters[`${getters['lsModuleName']}/at`](`selfcheck-first-choice-${categoryId}`) || null;
      },

      totalHistory: (state, getters) => {
        const result = {score: null, max: null, per: null, hasScore: false};
        const subjects = getters['subjects'];
        const historyHash = {};
        const subjectsHash = {};

        for (let subject of subjects) {
          const history = getters['subjectHistory'](subject.id);
          if (history.fixed && history.selected) {
            historyHash[subject.id] = history;
            subjectsHash[subject.id] = subject;
            result.max   += subject.max;
            result.score += history.score;
          }
        }

        for (let parentId in SCORE_ROUND_GROUPS) {
          const group = SCORE_ROUND_GROUPS[parentId];

          let allSelected = true;
          let groupTotal  = 0;
          let groupMax    = 0;

          for (let id of group) {
            const subject = subjectsHash[id];
            const history = historyHash[id];

            if (!history) {
              allSelected = false;
              break;
            }

            if (!history.fixed) {
              allSelected = false;
              break;
            }

            groupTotal += history.score;
            groupMax   += subject.max;
          }

          if (allSelected) {
            const parentSubject = subjectsHash[parentId];
            const parentHistory = historyHash[parentId];
            const roundedTotal  = Math.round(groupTotal * parentSubject.max / groupMax);
            result.score += roundedTotal - groupTotal;
            result.max   += parentSubject.max - groupMax;
          }
        }

        result.hasScore = result.max > 0;
        result.per = result.hasScore ? result.score / result.max : null;
        return result;
      },

      subjectSelectionErrors: () => selectedIds => subjectSelectionValidater(selectedIds),
    },

    actions: {
      clearAllHistories: ({ getters, dispatch }) => {
        return Promise.all([
          dispatch('clearFirstChoices'),
          dispatch('clearSubjectHistories'),
          dispatch('clearOptions'),
        ]);
      },
      clearFirstChoices: ({ getters, dispatch }) => {
        const categories = getters['groupedCategories'];
        const keys = categories.map(category => `selfcheck-first-choice-${category.id}`);
        return dispatch(`${getters['lsModuleName']}/deleteKeys`, keys, {root: true});
      },
      clearSubjectHistories: ({ getters, dispatch }) => {
        const subjects = getters['subjects'];
        if (!subjects.length) return;
        const promiseList = [];
        for (let subject of subjects) {
          promiseList.push(dispatch(`${getters['lsModuleName']}/deleteSelfcheckSubject`, subject.id, {root: true}));
          if (subject.children) {
            for (let child of subject.children) {
              promiseList.push(dispatch(`${getters['lsModuleName']}/deleteSelfcheckSubject`, child.id, {root: true}));
            }
          }
        }
        if (getters['segmentSync']) dn.segments.scores.erase();
        return Promise.all(promiseList);
      },
      clearOptions: ({ getters, dispatch }) => {
        return dispatch(`${getters['lsModuleName']}/delete`, 'self_check_attributes', {root: true});
      },
      setSubjectHistory: async ({ getters, dispatch }, {id, data, syncSelfcheckScore}) => {
        const currentHistory = getters['subjectHistory'](id, {advanceParams: false});
        const newData = Object.assign({}, currentHistory, data);

        if (syncSelfcheckScore) {
          if (syncSelfcheckScore === 'force' || typeof newData.directScore !== 'number') {
            newData.directScore = newData.selfcheckScore;
          }
        }

        await dispatch(`${getters['lsModuleName']}/setSelfcheckSubject`, {id, data: newData}, {root: true});
        const segmentSyncParams = {[id]: getters['subjectSegmentParams'](id)};
        if (getters['segmentSync']) dn.segments.scores.putSubjects(segmentSyncParams);
      },

      setGroupedCategoryFirstChoice: ({ getters, dispatch }, { categoryId, subjectId }) => {
        return dispatch(`${getters['lsModuleName']}/set`, {keyMap: `selfcheck-first-choice-${categoryId}`, value: subjectId}, {root: true});
      },

      syncAllSegment: async ({ getters, dispatch, commit }) => {
        if (!getters['segmentSync']) return;
        await dispatch('segments/ready', null, {root: true});
        const subjects = getters['subjects'];
        const syncData = {};
        for (let subject of subjects) {
          syncData[subject.id] = getters['subjectSegmentParams'](subject.id);
        }

        const updateData = dn.segments.scores.syncAndGetUpdateData(syncData);
        const promiseList = [];

        for (let id in updateData) {
          promiseList.push(dispatch('syncSubjectSegment', {id, data: updateData[id]}));
        }
        if (promiseList.length) return Promise.all(promiseList);
      },

      syncSubjectSegment: async ({ getters, dispatch, commit }, { id, data }) => {
        const subject = getters['subjectOrChildById'](id);
        if (!id) {
          console.warn(`セグメントの科目得点をローカルストレージに同期しようとしましたが、科目ID「${id}」は定義されていません。`);
          return;
        }

        const history = getters['subjectHistory'](id);
        const storeData = {};

        if (data.type !== undefined) storeData.type = data.type;
        if (data.state !== undefined) {
          if (data.state === 'fixed') {
            storeData.selected = true;
            if (history.isSelfCheck) {
              storeData.selfCheckIsFinished = true;
            }
          } else if (data.state === 'selected') {
            storeData.selected = true;
          } else if (data.state === 'pending') {
            storeData.selected = false;
            if (history.isSelfCheck) {
              storeData.selfCheckIsFinished = false;
            }
          }
        }

        if (data.direct !== undefined) storeData.directScore = data.direct;
        if (data.selfcheckScore !== undefined) storeData.selfcheckScore = data.selfcheck;
        return dispatch('setSubjectHistory', {id, data: storeData});
      },

      load: ({ getters, dispatch, commit }) => {
        if (getters['isLoaded']) return Promise.resolve();
        return new Promise(async (resolve, reject) => {
          if (getters['isLoading']) {
            ev.one('load', resolve);
            return;
          }

          // 自分の年度のローカルストレージのストアをルートモジュールに追加する
          await dispatch('useLsByPrefix', getters['year'], {root: true});

          try {
            commit('setLoadState', 'loading');
            const res = await $http.get(getters['subjectsFilePath']);
            const categories = parseXml(res.data);
            const childrenIds = [];

            for (let category of categories) {
              for (let subject of category.subjects) {
                if (subject.childrenId) childrenIds.push({subjectId: subject.id, childrenId: subject.childrenId});
              }
            }

            if (childrenIds.length) {
              await Promise.all(childrenIds.map(async ({ subjectId, childrenId }) => {
                const res = await $http.get(`${getters['dataDirPath']}/${childrenId}.xml`);
                commit('appendChildSubjects', {subjectId, xml: res.data});
                return null;
              }));
            }
            commit('setCategories', categories);
            commit('setLoadState', 'loaded');
            await dispatch('syncAllSegment');
            ev.emit('load');
            resolve();
          } catch(e) {
            commit('setLoadState', 'failid');
            reject(e);
          }
        });
      },
    },

    mutations: {

      setLoadState: (state, loadState) => {
        state.loadState = loadState;
      },

      setCategories: (state, _categories) => {
        _categories = _categories.filter(category => {
          if (!category.open) return false;
          category.subjects = category.subjects.filter(subject => subject.open);
          return category.open && !!category.subjects.length;
        });

        const categories = [], subjects = [];
        for (let _category of _categories) {
          const category = {
            id  : _category.id,
            name: _category.name,
          }

          for (let _subject of _category.subjects) {
            subjects.push({
              id        : _subject.id,
              categoryId: _category.id,
              name      : _subject.name,
              max       : _subject.max,
            })
          }
          categories.push(category);
        }

        Vue.set(state, 'categories', categories);
        Vue.set(state, 'subjects', subjects);
      },

      appendChildSubjects: (state, {subjectId, xml}) => {
        const children  = [];
        const $children = $(xml).find('kamoku');

        $children.each(function() {
          const $child = $(this);
          const $parts = $child.find('item');

          const child = {
            id       : $child.find('id').text(),
            subjectId: subjectId,
            name     : $child.find('name').text(),
            max      : parseInt($child.find('max_score').text(), 10),
            parts    : [],
          };

          $parts.each(function() {
            child.parts.push(parseInt($(this).text(), 10));
          });

          children.push(child);
        });

        Vue.set(state.childSubjects, subjectId, children);
      },
    },
  }
}



// WEBPACK FOOTER //
// ./src/js/store/modules/selfcheck/selfcheck-factory.js